import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function QCChecklistPage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Complete Kakobuy QC Spreadsheet Checklist for Quality Shopping 2026",
    "description": "Master the quality control process for Kakobuy purchases with our comprehensive QC checklist, photo review guide, and inspection tips from our trusted spreadsheet.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/qc-checklist"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20and%20a%20checklist%20clipboard%20resting%20on%20a%20clean%20white%20desk%20beside%20a%20pair%20of%20neatly%20folded%20garments%20and%20measuring%20tape%20soft%20natural%20lighting%20from%20a%20skylight%20minimalist%20quality%20inspection%20scene%20editorial%20photography%20with%20warm%20neutral%20background&width=1400&height=600&seq=article-qc-v2-02&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">QC Checklist</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              Complete <span className="text-primary-500">Kakobuy QC</span> Spreadsheet Checklist for Quality Shopping
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>6 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20and%20a%20checklist%20clipboard%20resting%20on%20a%20clean%20white%20desk%20beside%20a%20pair%20of%20neatly%20folded%20garments%20and%20measuring%20tape%20soft%20natural%20lighting%20from%20a%20skylight%20minimalist%20quality%20inspection%20scene%20editorial%20photography%20with%20warm%20neutral%20background&width=1400&height=600&seq=article-qc-v2-02&orientation=landscape"
                alt="Kakobuy QC Spreadsheet Checklist" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Why QC Matters for Kakobuy Shopping</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Quality control is the backbone of successful <strong>Kakobuy Spreadsheet</strong> shopping. Unlike traditional retail where you can inspect items in person, online purchases require you to rely on photos, descriptions, and seller reputation. This is where a proper QC process becomes invaluable. Our spreadsheet includes QC data for every listing, helping you verify product quality before you commit to a purchase.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> QC column flags products with detailed inspection photos, weight verification, and material quality assessments. Items with comprehensive QC documentation have a significantly lower rate of buyer disappointment, making them the safest choices for your shopping cart.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">The Complete QC Checklist</h2>
              <div className="space-y-3">
                {[
                  { title: "Stitching & Construction", desc: "Check seams, hemlines, and overall build quality. The Kakobuy Spreadsheet highlights items with reinforced stitching and clean finishes." },
                  { title: "Material & Fabric", desc: "Verify material composition matches the listing. Our spreadsheet notes discrepancies between advertised and actual materials." },
                  { title: "Logo & Branding", desc: "Examine logo placement, embroidery quality, and print accuracy. QC photos in the spreadsheet provide close-up views of all branding elements." },
                  { title: "Color Accuracy", desc: "Compare QC photos against listing images. Lighting can dramatically affect perceived color, so our spreadsheet includes daylight-balanced photos." },
                  { title: "Sizing Verification", desc: "Check measurements against the size chart. The spreadsheet includes actual measured dimensions from verified purchases." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-background-50">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}